import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, Loader2, KeyRound, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    if (password !== confirm) { toast.error("Passwords don't match."); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated — sign in with your new password");
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="reset-password-page">
      <div className="px-8 py-6">
        <div className="inline-flex items-center gap-2 text-slate-500">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <span className="text-xs uppercase tracking-widest">Set a new password</span>
        </div>
      </div>
      <div className="flex flex-1 items-center justify-center px-6">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-10 shadow-sm">
          {!token ? (
            <div className="text-center" data-testid="reset-password-notoken">
              <div className="font-display text-xl font-bold tracking-tight text-slate-900">Invalid reset link</div>
              <p className="mt-2 text-sm text-slate-600">This link is missing its token. Request a new one from the sign-in page.</p>
              <Link to="/forgot-password" className="mt-5 inline-block text-sm font-medium text-primary hover:underline">
                Request a new link →
              </Link>
            </div>
          ) : done ? (
            <div className="text-center" data-testid="reset-password-done">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
                <CheckCircle2 className="h-7 w-7 text-emerald-600" />
              </div>
              <div className="font-display text-xl font-bold tracking-tight text-slate-900">Password updated</div>
              <p className="mt-2 text-sm text-slate-600">Taking you to sign in…</p>
              <Link to="/login" className="mt-4 inline-block text-sm font-medium text-primary hover:underline" data-testid="reset-done-login-link">
                Sign in now →
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
                  <KeyRound className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <div className="font-display text-2xl font-bold tracking-tight text-slate-900">New password</div>
                  <p className="text-sm text-slate-500">Choose a password of at least 6 characters.</p>
                </div>
              </div>
              <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
                <label className="block">
                  <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">New password</span>
                  <input
                    type="password" required minLength={6} autoFocus value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="reset-password-input"
                    className="w-full h-12 rounded-lg border border-slate-200 bg-white px-4 text-base font-body focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
                  />
                </label>
                <label className="block">
                  <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">Confirm password</span>
                  <input
                    type="password" required minLength={6} value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="reset-password-confirm"
                    className="w-full h-12 rounded-lg border border-slate-200 bg-white px-4 text-base font-body focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
                  />
                </label>
                <button
                  type="submit" disabled={submitting}
                  data-testid="reset-password-submit"
                  className="w-full h-12 rounded-lg bg-primary text-white font-display text-base font-semibold tracking-tight transition active:scale-[0.98] hover:shadow disabled:opacity-60 inline-flex items-center justify-center gap-2"
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Update password
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
